import type { ReactNode } from 'react';
import type { Icon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * A detail-panel field caption: an icon, then the label with its definition
 * on hover (the dotted underline is the cue that there is one), then an
 * optional trailing control such as a notes button.
 */
export function FieldLabel({
  icon: LabelIcon,
  tooltip,
  trailing,
  children,
}: {
  icon: Icon;
  tooltip: string;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-4 items-center gap-1.5 text-xs font-medium text-muted-foreground">
      <LabelIcon className="h-3 w-3 shrink-0" />
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
      {/* Negative margin: a trailing button (e.g. notes) must not make this
          label taller than its neighbours, or side-by-side fields misalign. */}
      {trailing && (
        <span className="-my-1 inline-flex items-center">{trailing}</span>
      )}
    </div>
  );
}
